'use client';

import React, { memo, useCallback, useContext, useMemo } from 'react';
import SortSelect from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/components/ResultsToolbar/SortSelect/SortSelect.tsx';
import { Dictionary } from '@/src/lib/dictionary/types.ts';
import IppSelect from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/components/ResultsToolbar/SortSelect/IPPSelect.tsx';
import { usePathname, useRouter } from 'next/navigation';
import { CatalogRequestParams } from '@/src/lib/category/types.ts';
import { SelectOption } from '@/src/types/select-option.ts';
import { setCatalogSearchSettings } from '@/src/lib/catalog-search/business/catalog-search-settings.client.ts';
import { LayoutTemplateContext } from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/LayoutTemplateProvider.tsx';
type Props = {
  totalItems?: number;
  dict: Dictionary;
  sortParamLocalizedNames: Record<string, string>;
  currentSortParam: string | null;
  validItemsPerPageOptions: Array<number>;
  currentItemsPerPage: number;
};
const DEFAULT_SORT_VALUE = 'best_results';
const ResultsToolbarClient = memo(function ResultsToolbar({
  totalItems = 0,
  dict,
  sortParamLocalizedNames,
  currentSortParam,
  validItemsPerPageOptions,
  currentItemsPerPage
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    startTransition
  } = useContext(LayoutTemplateContext);
  const sortOptions = useMemo(() => [{
    value: DEFAULT_SORT_VALUE,
    label: dict.category.defaultSortBy
  }, ...Object.keys(sortParamLocalizedNames).map(key => ({
    value: key,
    label: sortParamLocalizedNames[key]
  }))], [dict, sortParamLocalizedNames]);
  const ippOptions = useMemo(() => validItemsPerPageOptions.map(option => ({
    value: option,
    label: option.toString()
  })), [validItemsPerPageOptions]);
  const onChange = useCallback(async (option: SelectOption, searchParam: CatalogRequestParams) => {
    if (searchParam === CatalogRequestParams.IPP && option.value === currentItemsPerPage || searchParam === CatalogRequestParams.SORT && option.value === currentSortParam) return;
    setCatalogSearchSettings({
      [searchParam]: option.value
    });
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(searchParam, option.value as string);
    searchParams.set(CatalogRequestParams.PAGE, '1');
    startTransition(() => {
      router.push(`${pathname}?${searchParams.toString()}`);
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [currentSortParam, pathname, router]);
  setCatalogSearchSettings({
    [CatalogRequestParams.SORT]: currentSortParam || DEFAULT_SORT_VALUE,
    [CatalogRequestParams.IPP]: currentItemsPerPage.toString()
  });
  return <div className="flex flex-col lg:flex-row lg:items-end [&+*]:mt-3 gap-3 -mt-5" data-testid="results-toolbar">
      <div className="lg:flex-grow">
        <div className="bg-gray-100 lg:h-[50px] flex flex-col lg:flex-row justify-between divide-y lg:divide-y-0 lg:divide-x divide-gray-300">
          <div className="flex flex-grow items-center py-4 lg:py-0 px-5">
            <div className="font-semibold">
              {dict.category.totalResults}: <span data-testid="total-item-count">{totalItems}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <SortSelect sortOptions={sortOptions} dict={dict} selectedOption={currentSortParam || DEFAULT_SORT_VALUE} onChange={onChange} />
        <IppSelect ippOptions={ippOptions} dict={dict} selectedOption={currentItemsPerPage} onChange={onChange} />
      </div>
    </div>;
});
export default ResultsToolbarClient;